import { type ProblemType } from '../models/level';
import { type ProgressMap, worldStars } from '../services/progress';

export interface GlossaryEntry {
  readonly slug: string;
  readonly term: string;
  readonly definition: string;
  readonly lore: string;
  readonly worldIndex: number;
}

type GlossarySource = Omit<GlossaryEntry, 'slug'>;

const ENTRIES: readonly GlossarySource[] = [
  {
    term: 'Alternating Path',
    definition:
      'A path that alternates between matched and unmatched edges, used to improve matchings.',
    lore: 'An operative moves safe-house to safe-house, stepping on and off the assignment roster with every junction — matched, unmatched, matched again. Kit calls it dancing; Pip calls it the only way to free a slot for a stranded agent.',
    worldIndex: 6,
  },
  {
    term: 'Augmenting Path',
    definition: 'A path from source to sink with available capacity, used to increase flow.',
    lore: 'Even a single pipe with spare capacity is enough to push one more unit of aether through the conduit network. Find it, fill it, and the resistance lights another district.',
    worldIndex: 4,
  },
  {
    term: 'Bipartite Graph',
    definition: 'A graph whose nodes split into two groups with edges only between groups.',
    lore: "Operatives on one side, cover identities on the other — no operative shares a cover, no cover hides two agents. The Wire Runners' assignment board is a bipartite graph made real.",
    worldIndex: 6,
  },
  {
    term: 'Capacity',
    definition: 'The maximum flow an edge can carry.',
    lore: 'Every aether conduit in Gearfall has a pressure rating stamped in brass. Push beyond it and the pipe ruptures — or worse, the Council detects the surge.',
    worldIndex: 4,
  },
  {
    term: 'Cut',
    definition: 'A set of edges whose removal disconnects source from sink.',
    lore: "A Wire Runner need only sever a handful of critical conduits to choke the Council's power supply. The art is finding which conduits those are — and cutting them before Varga notices.",
    worldIndex: 5,
  },
  {
    term: 'Cycle',
    definition: 'A path that starts and ends at the same node.',
    lore: 'Loops in the smuggling route waste precious time and risk doubling back past Council patrols. Pip always flags cycles — "We\'ve been here before, Kit!"',
    worldIndex: 1,
  },
  {
    term: 'Degree',
    definition: 'The number of edges connected to a node.',
    lore: 'A junction in the underbelly with many connecting tunnels is a hub of contraband traffic; one with a single exit is a dead end awaiting a Council raid.',
    worldIndex: 1,
  },
  {
    term: "Dijkstra's Algorithm",
    definition:
      'A greedy algorithm that finds the shortest path by always visiting the nearest unvisited node.',
    lore: 'Pip\'s brass abacus ticks through each junction in order of closeness, never revisiting a waypoint it has already settled. "Always take the nearest step first, Kit — panic and guessing costs lives."',
    worldIndex: 1,
  },
  {
    term: 'Directed Graph',
    definition: 'A graph where edges have a specific direction (one-way).',
    lore: 'Ziplines only go down, airship lanes run one way through the sky-corridors, and aether flows where the Council directs it — or where we reroute it. Direction matters in Gearfall.',
    worldIndex: 4,
  },
  {
    term: 'Edge',
    definition: 'A connection between two nodes, can have weight or capacity.',
    lore: "The sky-bridge, the aether conduit, the smuggler's tunnel — all are edges in the great network of Gearfall. Kit learned to read them as a bridge mechanic; now they're the resistance's lifelines.",
    worldIndex: 1,
  },
  {
    term: 'Flow',
    definition: 'The amount of material moving through an edge (limited by capacity).',
    lore: "Aether, supplies, coded messages — all obey the conservation law: what enters a junction must also leave it. The Wire Runners exploit this to redirect the Council's own resources.",
    worldIndex: 4,
  },
  {
    term: 'Ford-Fulkerson Method',
    definition:
      'Finds maximum flow by repeatedly finding and using augmenting paths until none remain.',
    lore: 'Round after round, Kit traces a fresh path through the Nexus and pushes aether until the pipes are full. When no path with spare capacity can be found, the maximum has been reached — and not a drop more can flow.',
    worldIndex: 4,
  },
  {
    term: 'Greedy Algorithm',
    definition:
      'An algorithm that makes the locally optimal choice at each step, hoping to find a global optimum.',
    lore: "Take the cheapest route available, always. It is not always correct, but for the problems the Wire Runners face most often it is fast enough to outrun Varga's patrols.",
    worldIndex: 1,
  },
  {
    term: "Kruskal's Algorithm",
    definition:
      "Builds a minimum spanning tree by greedily adding the cheapest edge that doesn't create a cycle.",
    lore: "Lay the shortest tether first, then the next shortest that reaches a new skyport — skip any line that would form a wasteful loop. The whole rigging network of the blockade runners was strung by Kruskal's own logic.",
    worldIndex: 2,
  },
  {
    term: 'Matching',
    definition: 'A set of edges with no shared nodes.',
    lore: 'To assign every operative to exactly one safe house, and every safe house to exactly one operative, is the classical matching problem. Get it wrong, and someone gets caught.',
    worldIndex: 6,
  },
  {
    term: 'Max-Flow Min-Cut Theorem',
    definition:
      'The maximum flow through a network equals the minimum cut capacity separating source from sink.',
    lore: "The Council's engineers boast of their aether throughput, never realising that Kit already knows its upper limit — for the tightest bottleneck in their network reveals the maximum they can ever push.",
    worldIndex: 5,
  },
  {
    term: 'Maximum Flow',
    definition: 'The greatest total flow from source to sink.',
    lore: "The conduit network must deliver maximum aether to the blacked-out districts whilst respecting every pipe's rated pressure. The resistance's survival depends on it.",
    worldIndex: 4,
  },
  {
    term: 'Minimum Cut',
    definition: 'The cut with the least total capacity (equals max flow).',
    lore: "By the theorem of Ford and Fulkerson, the bottleneck of the Council's network is precisely equal to the maximum flow it can sustain. Find the bottleneck, and you hold the power.",
    worldIndex: 5,
  },
  {
    term: 'Minimum Spanning Tree',
    definition: 'The spanning tree with the least total edge weight.',
    lore: 'To tether every skyport of the blockade with the least rigging line is to find the minimum spanning tree. Every metre of stolen line counts.',
    worldIndex: 2,
  },
  {
    term: 'Network Interdiction',
    definition:
      "Removing edges within a budget to maximally disrupt an adversary's optimal network usage.",
    lore: 'Varga cannot guard every conduit, so his agents choose surgically — snip the fewest pipes to hurt the resistance most. Knowing which edges he will target is half the battle of defending them.',
    worldIndex: 7,
  },
  {
    term: 'Node',
    definition: 'A point in a graph, representing a location or entity.',
    lore: 'Junction, valve, waypoint, or safe house — by whatever name, it is a point where paths converge and decisions are made. Kit sees them everywhere now.',
    worldIndex: 1,
  },
  {
    term: 'Path',
    definition: 'A sequence of edges connecting two nodes.',
    lore: "A Wire Runner may travel many routes between two districts; the skill is enumerating them all and choosing the one the Council isn't watching.",
    worldIndex: 1,
  },
  {
    term: 'Relaxation',
    definition: 'Updating the best-known distance to a node when a shorter path is found.',
    lore: 'Every time Pip discovers a faster route to a waypoint, she scratches out the old estimate and writes the new one. "Relax the distance, Kit — the first guess is rarely the best."',
    worldIndex: 1,
  },
  {
    term: 'Residual Graph',
    definition:
      'A graph showing remaining edge capacities after flow has been pushed through the network.',
    lore: 'After the first wave of aether flows through the Nexus, the residual graph reveals exactly where spare capacity still hides — and which pipes could carry a backward correction if needed.',
    worldIndex: 4,
  },
  {
    term: 'Shortest Path',
    definition: 'The path with minimum total weight between two nodes.',
    lore: "Among all routes from the Workshop to the drop point, one minimises risk and time alike. That is the Shortest Path — the Wire Runner's creed.",
    worldIndex: 1,
  },
  {
    term: 'Sink',
    definition: 'The destination node in flow or path problems.',
    lore: "All aether flows toward its destination; all network current is drawn toward the sink. In the resistance's hands, the sink is a blacked-out district waiting for light.",
    worldIndex: 4,
  },
  {
    term: 'Source',
    definition: 'The starting node in flow or path problems.',
    lore: "The source is the origin of all motion — the aether generator, the supply cache, Rekka's command post. Everything begins somewhere.",
    worldIndex: 4,
  },
  {
    term: 'Spanning Tree',
    definition: 'A subgraph connecting all nodes with no cycles.',
    lore: "A tree without cycles is the backbone of any efficient network — the skeleton upon which the blockade runners' rigging hangs.",
    worldIndex: 2,
  },
  {
    term: 'Travelling Salesman Problem',
    definition:
      'Find the shortest cycle visiting every node exactly once and returning to the start.',
    lore: "Every substation in the People's Grid needs a copper splice, and Rekka runs them all in a single night shift. Miss one and the block stays dark; visit one twice and the Council patrols catch the pattern. The shortest round trip that covers everything — that is the Travelling Salesman Problem, and it has no easy shortcut.",
    worldIndex: 3,
  },
  {
    term: 'Undirected Graph',
    definition: 'A graph where edges work in both directions.',
    lore: "A tunnel passable in both directions, a sky-bridge you can cross either way — these edges know no preferred orientation. The Council can't control what flows both ways.",
    worldIndex: 1,
  },
  {
    term: 'Weight',
    definition: 'A numerical value on an edge representing cost, distance, or time.',
    lore: "Risk rating, patrol density, aether pressure — the weight annotates each connection with the true cost of traversal. Pip keeps track so Kit doesn't have to.",
    worldIndex: 1,
  },
];

export function glossarySlug(term: string): string {
  return term
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export const GLOSSARY: readonly GlossaryEntry[] = ENTRIES.map((entry) => ({
  ...entry,
  slug: glossarySlug(entry.term),
}));

export function glossaryEntry(slug: string): GlossaryEntry | undefined {
  return GLOSSARY.find((entry) => entry.slug === slug);
}

export function isGlossaryEntryOpen(progress: ProgressMap, entry: GlossaryEntry): boolean {
  return entry.worldIndex === 1 || worldStars(progress, entry.worldIndex) > 0;
}

export function searchGlossary(query: string): readonly GlossaryEntry[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return GLOSSARY;
  return GLOSSARY.filter((entry) =>
    [entry.term, entry.definition, entry.lore].some((text) => text.toLowerCase().includes(needle)),
  );
}

export function glossaryTermForProblemType(type: ProblemType): string {
  switch (type) {
    case 'shortestPath':
    case 'shortestPathPlanar':
      return "Dijkstra's Algorithm";
    case 'mst':
      return "Kruskal's Algorithm";
    case 'tsp':
      return 'Travelling Salesman Problem';
    case 'maxFlow':
      return 'Ford-Fulkerson Method';
    case 'minCut':
      return 'Max-Flow Min-Cut Theorem';
    case 'matching':
      return 'Alternating Path';
    case 'interdiction':
      return 'Network Interdiction';
  }
}
